<template>
  <pl-dialog>
    <header
      class="text-gray-700 flex justify-between py-3 border-b px-7 items-center text-lg font-semibold"
    >
      <h6>
        Sync Assessment Data to
        <span class="capitalize">{{ selectedIntegration }}</span>
      </h6>
      <i
        class="fas fa-times text-gray-600 cursor-pointer hover:text-gray-900"
        @click="$emit('close')"
      ></i>
    </header>
    <div v-if="loading">
      <div class="flex justify-center my-56">
        <pl-spinner class="w-24 h-24" />
      </div>
    </div>
    <div v-else class="py-5 flex-grow overflow-y-auto">
      <div class="py-8 bg-gray-50">
        <header class="flex justify-center items-center mb-4">
          <img
            src="/static/logos/parlay_logo.svg"
            class="w-12 h-12 mx-5"
            alt="Parlay"
          />
          <i class="fa-solid fa-arrow-right-long text-gray-500 text-base"></i>
          <img :src="logo" alt="integration-logo" class="w-11 h-11 mr-2 mx-5" />
        </header>
        <h5 v-if="selectedIntegration" class="text-center text-base font-semibold text-gray-800">
          Sync Parlay to
          <span class="capitalize text-gray-800">{{ selectedIntegration }}</span>
          <!-- <i
              class="ml-2 cursor-pointer fa-solid fa-circle-info text-gray-500"
            ></i> -->
        </h5>
      </div>
      <div>
        <ul class="px-24 mt-7">
          <li
            v-for="(criteria, index) in sync_payload"
            :key="index"
            class="flex justify-between items-center py-2"
          >
            <div>
              <p class="font-medium">{{ criteria.title }}</p>
            </div>
            <div class="flex items-center justify-center">
              <pl-input
                v-model="criteria.value"
                :centeredText="true"
                type="integer"
                class="w-auto"
                :error="!validPercentages"
              />
              <span class="ml-3">%</span>
            </div>
          </li>
        </ul>
        <div v-if="edLink" class="flex justify-center pt-5">
          <input
            v-model="sync_feedback"
            type="checkbox"
            class="mr-2.5 cursor-pointer"
          />
          Also sync written feedback
        </div>
      </div>
    </div>
    <div v-if="!loading" class="flex justify-center items-center my-5">
      <button
        @click="$emit('close')"
        class="bg-gray-100 hover:bg-gray-200 px-6 py-2 mr-4 rounded"
      >
        Cancel
      </button>
      <button
        @click="syncAssessment()"
        class="px-6 py-2 bg-primary rounded text-white"
      >
        <span class="flex items-center">
          <span>Sync Data</span>
          <svg
            role="status"
            v-if="isSync === true"
            class="ml-2 w-4 h-4 text-white animate-spin"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="#E5E7EB"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentColor"
            />
          </svg>
        </span>
      </button>
    </div>
  </pl-dialog>
</template>

<script>
  import Vue from 'vue';
  import { mapGetters } from 'vuex';
  import { EventBus } from 'utils/event-bus';
  import adminServices from 'services/admin/adminServices';
  import googleClassroomService from 'services/googleClassroom/googleClassroomServices';

  export default Vue.extend({
    components: {},
    props: {
      logo: {
        type: String,
        required: true,
      },
      edLink: {
        type: Boolean,
      },
      activityType: {
        type: String,
        required: true,
      },
      selectedIntegration: {
        type: String,
        required: true,
      },
      integrationName: {
        type: String,
      },
    },

    data() {
      return {
        criteria_init_percent: 0,
        sync_feedback: false,
        sync_payload: [],
        maxPoints: 0,
        students_submissions: [],
        loading: false,
        percentage_error: 0.1,
        isSync: false,
      };
    },
    computed: {
      ...mapGetters([
        'getProfile',
        'getCurrentRT',
        'getCurrentDiscussion',
        'getCourseDetail',
      ]),
      validPercentages() {
        const values = this.sync_payload.map((p) => p.value);
        const percentages = values.reduce(
          (a, b) => parseFloat(a) + parseFloat(b),
          0
        );
        return (
          percentages > 100 - this.percentage_error &&
          percentages < 100 + this.percentage_error
        );
      },
      courseWorkId() {
        if (this.edLink) {
          const integration = this.currentActivity.edlink_integrations.find(
            (ed) => ed.provider_name.toLowerCase() === this.selectedIntegration
          );
          return integration ? integration.assignment_id : null;
        }
        if (this.$route.params.roundtableID)
          return this.getCurrentRT.google_classroom_course_work_id;
        if (this.$route.params.liveDiscussionID)
          return this.getCurrentDiscussion.google_classroom_course_work_id;
        return null;
      },
      maxScore() {
        return this.activityType === 'written'
          ? this.getCurrentRT.point_cap
          : this.getCurrentDiscussion.point_cap;
      },
      activityID() {
        return this.activityType === 'written'
          ? this.getCurrentRT.id
          : this.getCurrentDiscussion.id;
      },
      currentActivity() {
        if (this.activityType === 'written') return this.getCurrentRT;
        return this.getCurrentDiscussion;
      },
      integration() {
        return this.getCourseDetail.edlink_integrations.find(
          (x) => x.provider_name.toLowerCase() === this.selectedIntegration
        );
      },
      integrationActivity() {
        if (this.integration)
          return this.currentActivity.edlink_integrations.find(
            (x) => x.provider_name.toLowerCase() === this.selectedIntegration
          );
      },
    },
    methods: {
      syncEdlinkAssessment: async function () {
        const payload = {
          criteria: {},
          maxScore: this.maxPoints,
          send_feedback: this.sync_feedback,
        };
        this.sync_payload.forEach((criteria) => {
          payload.criteria[criteria.tag] = criteria.value / 100;
        });
        const { id: integration_id, course_id: edlink_course_id } =
          this.integration;
        const sync_job = await adminServices.syncSsoCourseAssignmentGrades(
          this.getCourseDetail.id,
          integration_id,
          edlink_course_id,
          this.activityType,
          this.activityID,
          this.courseWorkId,
          payload
        );
        const jobID = sync_job.data.id;
        EventBus.$emit('gradesEdlinkSyncJob', { jobID });
        this.$emit('close');
      },
      syncGoogleAssessment: async function () {
        this.isSync = true;
        const payload = {
          criteria: {},
          maxScore: this.maxPoints,
          course_id: this.getCourseDetail.id,
        };
        this.sync_payload.forEach((criteria) => {
          payload.criteria[criteria.tag] = criteria.value / 100;
        });
        const sync_job =
          await googleClassroomService.syncGoogleClassroomAssignmentGrades(
            this.getCourseDetail.id,
            this.getCourseDetail.google_classroom_integration.course_id,
            this.activityType,
            this.activityID,
            this.courseWorkId,
            payload
          );
        const jobID = sync_job.data.id;
        EventBus.$emit('gradesGoogleSyncJob', { jobID });
        this.isSync = false;
        this.$emit('close');
      },
      syncAssessment: async function () {
        if (this.maxScore) {
          if (this.edLink) this.syncEdlinkAssessment();
          else this.syncGoogleAssessment();
          const toastProperties = {
            type: 'Success',
            message: 'Successful synchronization with integration',
            time: 5000,
          };
          EventBus.$emit('createToast', toastProperties);
        } else {
          const toastProperties = {
            type: 'Warning',
            message: 'Set the Max Score on Settings Tab for each criteria.',
            time: 5000,
          };
          EventBus.$emit('createToast', toastProperties);
          this.$emit('close');
        }
      },
    },

    async created() {
      this.loading = true;
      if (!this.edLink) {
        const { data: course_work } = await googleClassroomService
          .getGoogleClassroomCourseAssignment(
            this.getCourseDetail.id,
            this.getCourseDetail.google_classroom_integration.course_id,
            this.activityType,
            this.activityID,
            this.courseWorkId
          )
          .catch((err) => {
            const message = err.response.data.error;
            if (message) EventBus.$emit('toast-msg', message);
          });
        await googleClassroomService
          .getGoogleClassroomAssignmentSubmissions(
            this.getCourseDetail.id,
            this.getCourseDetail.google_classroom_integration.course_id,
            this.activityType,
            this.activityID,
            this.courseWorkId
          )
          .then((data) => {
            this.students_submissions = data.students_submissions;
            this.loading = false;
            this.maxPoints = course_work.maxPoints;
          })
          .catch((err) => {
            this.loading = false;
            const message = err.response.data.error;
            if (message) EventBus.$emit('toast-msg', message);
          });
      } else {
        const { id: integration_id, course_id: edlink_course_id } =
          this.integration;
        const { assignment_id } = this.integrationActivity;
        const { data: assignment } = await adminServices
          .getSsoCourseAssignment(
            this.getCourseDetail.id,
            integration_id,
            edlink_course_id,
            this.activityType,
            this.activityID,
            assignment_id
          )
          .catch((err) => {
            this.loading = false;
            const message = err.response.data.error;
            if (message) EventBus.$emit('toast-msg', message);
          });
        this.loading = false;
        this.maxPoints = assignment.points_possible;
      }
    },
    mounted() {
      const rewards =
        this.activityType === 'written'
          ? this.getCurrentRT.points_settings.peer_rewards
          : this.getCurrentDiscussion.points_settings.peer_rewards;
      this.criteria_init_percent = 100.0 / rewards.length;
      rewards.forEach((peer_reward) => {
        const obj = {
          tag: peer_reward.tag,
          title: peer_reward.title,
          value: this.criteria_init_percent,
        };
        this.sync_payload.push(obj);
      });
    },
  });
</script>
<style lang="scss" scoped>
  input[type='checkbox'] {
    width: 1.25rem !important;
    height: 1.25rem !important;
    -webkit-appearance: none;
    -moz-appearance: none;
    -o-appearance: none;
    appearance: none;
    box-shadow: none;
    font-size: 0.8rem;
    text-align: center;
    line-height: 1.25rem;
    border-radius: 0.25rem;
    --tw-bg-opacity: 1;
    background-color: rgba(243, 244, 246, var(--tw-bg-opacity));
  }
  input[type='checkbox']:checked {
    background-color: #72ccd7;
  }
  input[type='checkbox']:checked::after {
    font-family: 'Font Awesome 5 Free';
    content: '\f00c';
    font-weight: 900;
    color: white;
    line-height: 1.25rem;
  }
</style>



// WEBPACK FOOTER //
// syncAssessmentDataModal.vue?472aadac